import { type ParsedToken, parseQuery } from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { FieldOptions } from '@gonfalon/dataviz-experimental/types';

import { CONTEXT_ATTRS_PREFIX, CONTEXT_KIND_FIELD } from './metricEventFilterFields';
import { displayFieldName, fieldCategoryFor } from './suggestionFieldOptions';

export function isContextField(field: string): boolean {
  return field === CONTEXT_KIND_FIELD || field.startsWith(CONTEXT_ATTRS_PREFIX);
}

export function committedContextAttributeFields(tokens: ParsedToken[]): string[] {
  const fields: string[] = [];
  const walk = (list: ParsedToken[]) => {
    for (const token of list) {
      if (token.type === 'FILTER' && token.field.startsWith(CONTEXT_ATTRS_PREFIX)) {
        fields.push(token.field);
      } else if (token.type === 'EXPRESSION') {
        walk(token.inner);
      }
    }
  };
  walk(tokens);
  return fields;
}

export function seedFieldOptions(query?: string): FieldOptions[] {
  if (!query) {
    return [];
  }
  const seen = new Set<string>();
  const options: FieldOptions[] = [];
  const walk = (tokens: ParsedToken[]) => {
    for (const token of tokens) {
      if (token.type === 'FILTER' && token.field !== CONTEXT_KIND_FIELD) {
        if (!seen.has(token.field)) {
          seen.add(token.field);
          options.push({
            field: token.field,
            displayValue: displayFieldName(token.field),
            fieldCategory: fieldCategoryFor(token.field),
            allowedOperators: ['=', '!='],
            sampleValues: [],
            allowCustom: true,
          });
        }
      } else if (token.type === 'EXPRESSION') {
        walk(token.inner);
      }
    }
  };
  walk(parseQuery(query));
  return options;
}

export function mergeSeedFieldOptions(fetched: FieldOptions[], seeds: FieldOptions[]): FieldOptions[] {
  if (seeds.length === 0) {
    return fetched;
  }
  const present = new Set(fetched.map((option) => option.field));
  const extra = seeds.filter((seed) => !present.has(seed.field));
  return extra.length > 0 ? [...fetched, ...extra] : fetched;
}

export function scopeFieldOptions(fieldOptions: FieldOptions[], selectedKinds: string[]): FieldOptions[] {
  if (selectedKinds.length === 0) {
    return fieldOptions;
  }
  const kinds = new Set(selectedKinds);
  return fieldOptions.filter((option) => {
    if (option.field === CONTEXT_KIND_FIELD) {
      return true;
    }
    if (option.field.startsWith(CONTEXT_ATTRS_PREFIX)) {
      const kind = option.field.slice(CONTEXT_ATTRS_PREFIX.length).split('.')[0];
      return kinds.has(kind);
    }
    return true;
  });
}
